import { ITicketRoutingInfo } from "@iloq/shared";
import { required } from "vuelidate/lib/validators";

export interface TicketCategory {
  name: string;
  subCategories?: TicketCategory[];
  requiredAdditionalInfos?: string[];
  requiredAttachments?: string[];
  instructionTexts?: string[];
  ticketRoutingInfo?: ITicketRoutingInfo; // Only has an effect when no subCategories exist
}

// Note: Names need to be unique
export const ticketCategories: TicketCategory[] = [
  {
    name: "shipmentRelated",
    subCategories: [
      {
        name: "shipmentRelated.shipmentInquiry",
        requiredAdditionalInfos: ["orderNumber"],
        ticketRoutingInfo: ITicketRoutingInfo.Shipment_Inquiry,
      },
    ],
  },
  {
    name: "orderRelated",
    subCategories: [
      {
        name: "orderRelated.shippedOrder",
        requiredAdditionalInfos: ["orderNumber"],
        subCategories: [
          {
            name: "orderRelated.shippedOrder.wrongPrice",
            requiredAdditionalInfos: ["invoiceNumber"],
            ticketRoutingInfo: ITicketRoutingInfo.Order_CorrectShipped,
          },
          {
            name: "orderRelated.shippedOrder.wrongQuantity",
            requiredAdditionalInfos: ["invoiceNumber"],
            ticketRoutingInfo: ITicketRoutingInfo.Order_CorrectShipped,
          },
          {
            name: "orderRelated.shippedOrder.wrongProduct",
            requiredAdditionalInfos: ["wrongItemSku", "correctItemSku"],
            ticketRoutingInfo: ITicketRoutingInfo.Order_CorrectShipped,
          },
        ],
      },
      {
        name: "orderRelated.nonShippedOrder",
        requiredAdditionalInfos: ["orderNumber", "projectId"],
        ticketRoutingInfo: ITicketRoutingInfo.Order_ChangeNonShipped,
      },
      /*
      TODO this has no mapping in spec excel
      {
        name: "orderRelated.other",
        ticketRoutingInfo: TicketRoutingInfo.MISSING,
      },
       */
    ],
  },
  {
    name: "partnerPortalRelated",
    requiredAdditionalInfos: ["userEmail"],
    subCategories: [
      {
        name: "partnerPortalRelated.portalError",
        instructionTexts: ["portalErrorInstruction"],
        ticketRoutingInfo: ITicketRoutingInfo.PartnerPortal,
      },
      {
        name: "partnerPortalRelated.portalAccess",
        requiredAdditionalInfos: ["usersName", "requiredRoles"],
        requiredAttachments: ["signedAccessRequestForm"],
        ticketRoutingInfo: ITicketRoutingInfo.PartnerPortal,
      },
    ],
  },
  {
    name: "licenseRelated",
    requiredAdditionalInfos: ["agreementNumber"],
    subCategories: [
      {
        name: "licenseRelated.licenseDowngrade",
        ticketRoutingInfo: ITicketRoutingInfo.License_UpgradeDowngrade,
      },
      {
        name: "licenseRelated.apiUpgrade",
        requiredAdditionalInfos: ["integratorsName"],
        ticketRoutingInfo: ITicketRoutingInfo.License_UpgradeDowngrade,
      },
      {
        name: "licenseRelated.credentialsAndAccess",
        requiredAdditionalInfos: ["userEmail", "usersName"],
        ticketRoutingInfo: ITicketRoutingInfo.License_Access,
      },
      {
        name: "licenseRelated.ownershipChange",
        requiredAttachments: ["signedAgreementTransferForm"],
        ticketRoutingInfo: ITicketRoutingInfo.License_OwnershipChange,
      },
      {
        name: "licenseRelated.other",
        ticketRoutingInfo: ITicketRoutingInfo.License_Other,
      },
    ],
  },
  {
    name: "returnRelated",
    requiredAdditionalInfos: ["returnId"],
    subCategories: [
      {
        name: "returnRelated.maintenanceReturn",
        ticketRoutingInfo: ITicketRoutingInfo.Return_MaintenanceReturn,
      },
      {
        name: "returnRelated.salesReturn",
        requiredAdditionalInfos: ["orderNumber"],
        ticketRoutingInfo: ITicketRoutingInfo.Return_SalesReturn,
      },
    ],
  },
  {
    name: "technicalSupport",
    ticketRoutingInfo: ITicketRoutingInfo.TechnicalSupportWithoutCountryCode,
  },
  {
    name: "financialInquiry",
    subCategories: [
      {
        name: "financialInquiry.invoiceCopies",
        requiredAdditionalInfos: ["invoiceNumber"],
        ticketRoutingInfo: ITicketRoutingInfo.FinancialInquiry, // TODO waiting for FinancialInquiry variations specs from iLOQ but the first version can be released this way
      },
      {
        name: "financialInquiry.invoiceContentInquiry",
        requiredAdditionalInfos: ["invoiceNumber"],
        ticketRoutingInfo: ITicketRoutingInfo.FinancialInquiry, // TODO waiting for FinancialInquiry variations specs from iLOQ but the first version can be released this way
      },
      {
        name: "financialInquiry.paymentRelated",
        requiredAdditionalInfos: ["invoiceNumber"],
        ticketRoutingInfo: ITicketRoutingInfo.FinancialInquiry, // TODO waiting for FinancialInquiry variations specs from iLOQ but the first version can be released this way
      },
      {
        name: "financialInquiry.billingInformationAndTaxes",
        ticketRoutingInfo: ITicketRoutingInfo.FinancialInquiry, // TODO waiting for FinancialInquiry variations specs from iLOQ but the first version can be released this way
      },
    ],
  },
];

export const getCategoryNames = (categories: TicketCategory[]) =>
  categories.map((category) => category.name);

const flatten = (categories: TicketCategory[], result: { [key: string]: TicketCategory } = {}) => {
  categories.forEach((category) => {
    result[category.name] = category;
    if (category.subCategories) {
      flatten(category.subCategories, result);
    }
  });
  return result;
};
const categoriesByName = flatten(ticketCategories);

export const getCategoryByName = (name: string) => categoriesByName[name];

/**
 * Returns an array of selected categories if the last category is a leaf category (which means that
 * the category selection is "ready"). Otherwise, returns undefined.
 * @param categories an array containing selected categories and possibly nulls (after them)
 */
export const getSelectedFullCategoryPath = (
  categories: (TicketCategory | null)[]
): TicketCategory[] | undefined => {
  const nonNullCategories = categories.filter((category) => category !== null) as TicketCategory[];
  if (
    nonNullCategories.length &&
    nonNullCategories[nonNullCategories.length - 1].ticketRoutingInfo
  ) {
    // Last category is a leaf if it contains ticketRoutingInfo
    return nonNullCategories;
  }
  return undefined;
};

export const getAllPossibleDynamicFieldsAsObjectWithEmptyValues = () => {
  const result: { [key: string]: string } = {};
  Object.values(categoriesByName).forEach((category) => {
    category.requiredAdditionalInfos?.forEach((field) => {
      result[field] = "";
    });
  });
  return result;
};
